var RepeaterPaddedListArranger = require("enyo-ui/RepeaterPaddedListArranger"),
    kind                       = require("enyo/kind");

/**
 * [ColumnatedListArranger]{@link app.ui.repeaters.ColumnatedListArranger}
 * extends `enyo-ui.RepeaterPaddedListArranger` to divide the viewport into
 * columns or rows the same way `enyo-ui.RepeaterPaddedGridArranger` does. This
 * way the children in a list can be scaled to align with adjacent grids.
 *
 * @ui
 * @class   app.ui.repeaters.ColumnatedListArranger
 * @extends enyo-ui.RepeaterPaddedListArranger
 * @public
 */
module.exports = kind(/** @lends  app.ui.repeaters.ColumnatedListArranger.prototype */ {

    /**
     * @private
     */
    name: "app.ui.repeaters.ColumnatedListArranger",

    /**
     * @private
     */
    kind: RepeaterPaddedListArranger,

    /**
     * @private
     */
    options: /** @lends  app.ui.repeaters.ColumnatedListArranger.prototype */ {

        /**
         * Sets the step size with which the amount of rows/columns dividing the
         * viewport should be incremented.
         *
         * For example: A `divisionStep` value of 2 will divide the viewport
         * into 0, 2, 4, 6 etc. columns/rows.
         *
         * @type     {number}
         * @default  1
         * @public
         */
        divisionStep: 1,

        /**
         * Sets an optional offset to apply when dividing the viewport into
         * rows/columns. The offset value must be positive and will only be
         * applied if the viewport can accommodate 1 or more rows/columns.
         *
         * For example: A [`divisionStep`]{@link
         * app.ui.repeaters.ColumnatedListArranger#divisionStep} value of 1, and
         * a `divisionOffset` value of 0.5 will result in 0, 1.5, 2.5, 3.5 etc.
         * columns/rows.
         *
         * @type     {number}
         * @default  0
         * @public
         */
        divisionOffset: 0,

        /**
         * If `direction` option is set to "horizontal", the `viewportWidth`
         * will be used to calculate the width of repeater children. The list
         * will use the smallest amount of children that can be drawn within the
         * `viewportWidth` without exceeding `maxChildWidth`.
         *
         * By default, we assume the viewport equals the list's boundaries.
         * Override this option if this is not the case.
         *
         * @return  {number}  The viewport width in pixels
         * @public
         */
        viewportWidth: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientWidth : 0;
        },

        /**
         * If `direction` option is set to "vertical", the `viewportHeight` will
         * be used to calculate the height of repeater children. The list will
         * use the smallest amount of children that can be drawn within the
         * `viewportHeight` without exceeding `maxChildHeight`.
         *
         * By default, we assume the viewport equals the list's boundaries.
         * Override this option if this is not the case.
         *
         * @return  {number}  The viewport height in pixels
         * @public
         */
        viewportHeight: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientHeight : 0;
        },

        /**
         * The maximum width of a child in pixels.
         *
         * @type     {number}
         * @default  Infinity
         * @public
         */
        maxChildWidth: Infinity,

        /**
         * The maximum height of a child in pixels.
         *
         * @type     {number}
         * @default  Infinity
         * @public
         */
        maxChildHeight: Infinity,

        /**
         * Whether to round values of cell width/height and arrangement
         * transform to integer pixels values.
         *
         * @type    {number}
         * @default false
         * @public
         */
        roundPixels: false
    },

    /**
     * @private
     */
    reflow: function() {
        var childContainer = this.repeater.getChildContainer(),
            direction      = this.getOption("direction"),
            childSpacing   = this.getOption("childSpacing");

        // Container required
        if (!childContainer.hasNode()) {
            return;
        }

        // If we're using placeholders, use placeholderDimensions as
        // defaultChildSize.
        if (this.layoutPlaceholder) {
            switch (this.getOption("direction")) {
                case "horizontal":
                    this.calcPlaceholderWidth();
                    this.calcPlaceholderHeight();
                    break;
                case "vertical":
                default:
                    this.calcPlaceholderHeight();
                    this.calcPlaceholderWidth();
                    break;
            }

            // Reset childSizeManager to use the standard child size
            this.childSizeManager.reset(direction === "vertical"
                ? this.placeholderHeight + childSpacing
                : this.placeholderWidth + childSpacing);
        }

        // No placeholders, just use defaultChildSize option.
        else {
            this.childSizeManager.reset(this.getOption("defaultChildSize") + childSpacing);
        }
    },

    /**
     * Calculate the width of a placeholder.
     *
     * @private
     */
    calcPlaceholderWidth: function() {
        var childSpacing,
            viewportWidth,
            padBefore,
            padAfter,
            maxChildWidth,
            maxChildHeight,
            step,
            offset,
            availableWidth,
            columns,
            placeholderWidth,
            placeholderHeight;

        childSpacing = this.getOption("childSpacing");

        switch (this.getOption("direction") || "vertical") {
            case "horizontal":
                viewportWidth  = this.getOption("viewportWidth");
                padBefore      = this.getOption("padBefore");
                padAfter       = this.getOption("padAfter");
                maxChildWidth  = this.getOption("maxChildWidth");
                maxChildHeight = this.getOption("maxChildHeight");
                step           = this.getOption("divisionStep");
                offset         = this.getOption("divisionOffset");

                // The maxChildHeight might force a lower maxChildWidth.
                maxChildWidth = Math.min(maxChildWidth, this.layoutPlaceholder.calcWidth(maxChildHeight));

                // The width that's available to divide into columns
                availableWidth = viewportWidth
                    + childSpacing // One childSpacing worth of overflow
                    - padBefore // Compensate for left padding
                    - padAfter; // Compensate for right padding

                if (availableWidth <= 0 || maxChildWidth <= 0 || step <= 0) {
                    placeholderWidth = 0;
                }
                else {
                    offset           = offset < 0 ? 0 : offset;
                    step             = step <= 0 ? 1 : step;
                    columns          = availableWidth / (maxChildWidth + childSpacing);
                    columns          = Math.ceil(columns / step) * step;
                    placeholderWidth = (availableWidth / (columns + offset)) - childSpacing;
                }
                break;
            case "vertical":
            default:
                placeholderHeight = this.placeholderHeight;
                placeholderWidth  = Math.max(0, this.layoutPlaceholder.calcWidth(placeholderHeight));
                break;
        }

        this.placeholderWidth = placeholderWidth;
    },

    /**
     * Calculate the height of a row.
     *
     * @private
     */
    calcPlaceholderHeight: function() {
        var childSpacing,
            viewportHeight,
            padBefore,
            padAfter,
            maxChildWidth,
            maxChildHeight,
            step,
            offset,
            availableHeight,
            rows,
            placeholderHeight,
            placeholderWidth;

        childSpacing = this.getOption("childSpacing");

        switch (this.getOption("direction") || "vertical") {
            case "horizontal":
                placeholderWidth  = this.placeholderWidth;
                placeholderHeight = Math.max(0, this.layoutPlaceholder.calcHeight(placeholderWidth));
                break;
            case "vertical":
            default:
                viewportHeight = this.getOption("viewportHeight");
                padBefore      = this.getOption("padBefore");
                padAfter       = this.getOption("padAfter");
                maxChildWidth  = this.getOption("maxChildWidth");
                maxChildHeight = this.getOption("maxChildHeight");
                step           = this.getOption("divisionStep");
                offset         = this.getOption("divisionOffset");

                // The maxChildWidth might force a lower maxChildHeight.
                maxChildHeight = Math.min(maxChildHeight, this.layoutPlaceholder.calcWidth(maxChildWidth));

                // The height that's available to divide into rows
                availableHeight = viewportHeight
                    + childSpacing // One childSpacing worth of overflow
                    - padBefore // Compensate for top padding
                    - padAfter; // Compensate for bottom padding

                if (availableHeight <= 0 || maxChildHeight <= 0 || step <= 0) {
                    placeholderHeight = 0;
                }
                else {
                    offset            = offset < 0 ? 0 : offset;
                    step              = step <= 0 ? 1 : step;
                    rows              = availableHeight / (maxChildHeight - childSpacing);
                    rows              = Math.ceil(rows / step) * step;
                    placeholderHeight = (availableHeight / (rows + offset)) - childSpacing;
                }
                break;
        }

        this.placeholderHeight = placeholderHeight;
    },

    /**
     * Get the size of the entire arrangement. Should have at least a width or
     * height property.
     *
     * @return  {Object}  The arrangement size.
     * @method
     * @public
     */
    getArrangementSize: kind.inherit(function(sup) {
        return function() {
            var size              = sup.apply(this, arguments),
                roundPixels       = this.getOption("roundPixels"),
                placeholderHeight = this.placeholderHeight,
                placeholderWidth  = this.placeholderWidth;

            switch (this.getOption("direction")) {
                case "horizontal":
                    size.height = roundPixels ? Math.floor(placeholderHeight) : placeholderHeight;
                    size.width  = roundPixels ? Math.floor(size.width) : size.width;
                    break;
                case "vertical":
                default:
                    size.width  = roundPixels ? Math.floor(placeholderWidth) : placeholderWidth;
                    size.height = roundPixels ? Math.floor(size.height) : size.height;
                    break;
            }

            return size;
        };
    }),

    /**
     * Returns the distance (in pixels) between the top edge of the child at the
     * given index, and the top edge of the childContainer.
     *
     * @param   {number}  index  The index
     * @return  {number}  The offset
     * @method
     * @public
     */
    getOffsetTop: kind.inherit(function(sup) {
        return function(index) {
            var offset      = sup.call(this, index),
                roundPixels = this.getOption("roundPixels");

            return roundPixels ? Math.floor(offset) : offset;
        };
    }),

    /**
     * Returns the distance (in pixels) between the left edge of the child at
     * the given index, and the left edge of the childContainer.
     *
     * @param   {number}  index  The index
     * @return  {number}  The offset
     * @method
     * @public
     */
    getOffsetLeft: kind.inherit(function(sup) {
        return function(index) {
            var offset      = sup.call(this, index),
                roundPixels = this.getOption("roundPixels");

            return roundPixels ? Math.floor(offset) : offset;
        };
    }),

    /**
     * Renders the child if needed and sizes it along the list's axis that does
     * not expand. Gets the child's resulting size on the expanding axis and
     * stores it on the child itself.
     *
     * @param  {enyo.Control}  child  The child
     * @public
     */
    drawChild: function(child) {
        var childContainer    = this.repeater.getChildContainer(),
            rendered          = child.hasNode(),
            direction         = this.getOption("direction"),
            roundPixels       = this.getOption("roundPixels"),
            placeholderHeight = this.placeholderHeight,
            placeholderWidth  = this.placeholderWidth,
            childWidth,
            childHeight,
            childBounds;

        // Determine the child's bounds
        switch (direction) {
            case "horizontal":
                childHeight = placeholderHeight;
                childWidth  = child.calcWidth(placeholderHeight);
                break;
            case "vertical":
            default:
                childHeight = child.calcHeight(placeholderWidth);
                childWidth  = placeholderWidth;
                break;
        }
        childBounds = {
            width:  roundPixels ? Math.floor(childWidth) : childWidth,
            height: roundPixels ? Math.floor(childHeight) : childHeight
        };

        // Size child
        child.setBounds(childBounds);

        // For use internal to the arranger.
        if (direction === "vertical") {
            child.set("repeaterSize", childBounds.height || undefined);
        }
        else {
            child.set("repeaterSize", childBounds.width || undefined);
        }

        // For use outside of the arranger.
        child.set("repeaterBounds", childBounds);

        // Render child as needed.
        if (childContainer.hasNode() && !rendered) {
            child.canGenerate = true;
            child.addStyles({
                position: "absolute",
                top:      0,
                left:     0
            });
            child.render();
        }
    }

});
